import { defineStore } from 'pinia'
import { ref } from 'vue'
import { updatePrimaryPalette, palette } from '@primevue/themes'
import dashboardService from '@/services/dashboardService'

// Favicon is a per-client asset served same-origin from the branding volume.
const FAVICON_URL = `${import.meta.env.BASE_URL}branding/favicon.ico`

function applyBranding(config) {
  if (config.site_title) {
    document.title = config.site_title
  }
  if (config.site_description) {
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', config.site_description)
  }
  // Point the favicon at the branding volume (also fixes the /<slug>/ path on stage).
  let icon = document.querySelector('link[rel="icon"]')
  if (!icon) {
    icon = document.createElement('link')
    icon.setAttribute('rel', 'icon')
    document.head.appendChild(icon)
  }
  icon.setAttribute('href', FAVICON_URL)
  // Apply the client's accent color to the whole theme at runtime.
  if (config.primary_color) {
    try {
      const ramp = palette(config.primary_color)
      // PrimeVue component theme.
      updatePrimaryPalette(ramp)
      // Tailwind v4 emits colour utilities as var(--color-primary-*), so
      // overriding those custom properties retints every bg-/text-/border-/
      // from-/to-primary usage (hero gradient, nav, buttons) to the client's
      // colour too — not just PrimeVue components.
      Object.keys(ramp).forEach((shade) => {
        document.documentElement.style.setProperty(
          `--color-primary-${shade}`, ramp[shade]
        )
      })
    } catch (e) {
      console.warn('Could not apply primary_color to theme:', e)
    }
  }
}

export const useConfigStore = defineStore('config', () => {
  // State
  const config = ref({
    brand_name: 'EnerCare',
    site_title: 'EnerCare - Portal Klienta',
    site_description: 'EnerCare - zarządzaj swoimi kontami energetycznymi, przeglądaj faktury i monitoruj zużycie',
    logo_path: '/static/enercare/logo.png',
    primary_color: '#4CAF50',
    brand_url: '',
    privacy_policy_url: '',
    terms_of_service_url: '',
    payments_enabled: true,
    deadline_time: '09:30',
    deadline_timezone: 'Europe/Warsaw'
  })
  const isLoading = ref(false)
  const isLoaded = ref(false)

  // Actions
  async function loadConfig() {
    if (isLoading.value || isLoaded.value) return

    try {
      isLoading.value = true
      const response = await dashboardService.getConfig()
      config.value = response.data
      applyBranding(config.value)
      isLoaded.value = true
    } catch (error) {
      console.error('Failed to load configuration:', error)
    } finally {
      isLoading.value = false
    }
  }

  return {
    config,
    isLoading,
    isLoaded,
    loadConfig
  }
})
